import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, Maximize2, Crop as CropIcon } from "lucide-react";

/**
 * Swipeable product gallery.
 * - Touch swipe + mouse drag between images
 * - Prev/next arrows
 * - Thumbnail row below (click to jump)
 * - Toggle: FILL vs FIT (object-cover vs object-contain) so buyers can see the whole photo
 *
 * Props:
 *   images: string[]
 *   alt: string
 *   soldOut: boolean
 *   overlay: ReactNode (stickers, SOLD stamp)
 */
export default function ProductGallery({ images = [], alt = "", soldOut = false, overlay = null, onImageClick }) {
  const [idx, setIdx] = useState(0);
  const [fill, setFill] = useState(false); // false = contain (default, full photo), true = cover (crop to fill)
  const startX = useRef(null);
  const trackRef = useRef(null);

  const total = images.length;
  const go = (i) => setIdx(((i % total) + total) % total);
  const prev = () => go(idx - 1);
  const next = () => go(idx + 1);

  // Keyboard arrows
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, total]);

  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (Math.abs(dx) > 40) { dx > 0 ? prev() : next(); }
    startX.current = null;
  };
  const onMouseDown = (e) => { startX.current = e.clientX; };
  const onMouseUp = (e) => {
    if (startX.current == null) return;
    const dx = e.clientX - startX.current;
    if (Math.abs(dx) > 40) { dx > 0 ? prev() : next(); }
    startX.current = null;
  };

  if (!total) {
    return <div className="w-full aspect-[3/4] bg-[#F4F4F0] border-2 border-black" />;
  }

  return (
    <div data-testid="product-gallery">
      <div
        ref={trackRef}
        className="relative border-2 border-black brutal-shadow bg-white select-none overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
      >
        <div className="relative w-full aspect-[3/4] bg-[#F4F4F0] flex items-center justify-center">
          <img
            src={images[idx]}
            alt={alt}
            data-testid="pd-image"
            draggable={false}
            onClick={() => onImageClick?.(idx)}
            className={`max-w-full max-h-full w-full h-full ${fill ? "object-cover" : "object-contain"} ${soldOut ? "grayscale" : ""} ${onImageClick ? "cursor-zoom-in" : ""}`}
            style={{ objectPosition: "center center" }}
          />
          {overlay}
          {/* Nav arrows */}
          {total > 1 && (
            <>
              <button
                type="button"
                data-testid="pd-prev"
                onClick={prev}
                aria-label="Previous photo"
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/95 border-2 border-black w-9 h-9 flex items-center justify-center hover-nudge"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                data-testid="pd-next"
                onClick={next}
                aria-label="Next photo"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/95 border-2 border-black w-9 h-9 flex items-center justify-center hover-nudge"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
          {/* Fit/Fill toggle */}
          <button
            type="button"
            data-testid="pd-fit-toggle"
            onClick={(e) => { e.stopPropagation(); setFill((f) => !f); }}
            aria-label={fill ? "Show whole photo" : "Fill the frame"}
            title={fill ? "Show whole photo" : "Fill (crop)"}
            className="absolute bottom-2 right-2 bg-black text-[#D4FF00] border-2 border-black px-2 py-1 font-mono-b text-[10px] uppercase font-bold inline-flex items-center gap-1 hover-nudge"
          >
            {fill ? <><Maximize2 className="w-3 h-3" /> FIT</> : <><CropIcon className="w-3 h-3" /> FILL</>}
          </button>
          {/* Counter */}
          {total > 1 && (
            <div className="absolute bottom-2 left-2 bg-black text-[#D4FF00] border-2 border-black px-2 py-1 font-mono-b text-[10px] uppercase font-bold" data-testid="pd-counter">
              {idx + 1} / {total}
            </div>
          )}
        </div>
      </div>

      {/* Thumbnails */}
      {total > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto">
          {images.map((im, i) => (
            <button
              type="button"
              key={im}
              data-testid={`pd-thumb-${i}`}
              onClick={() => go(i)}
              aria-label={`Photo ${i + 1}`}
              className={`shrink-0 w-20 h-20 border-2 border-black bg-[#F4F4F0] ${i === idx ? "brutal-shadow-sm ring-2 ring-[#D4FF00] ring-inset" : ""}`}
            >
              <img src={im} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
